import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  createTransform,
  persistReducer,
  persistStore,
} from 'redux-persist';
import type { PersistConfig, PersistedState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { authReducer } from '@/entities/session';
import type { AuthState } from '@/entities/session';
import { reducer as onboardingReducer } from '@/features/onboarding';
// Imported past the page's index.ts: the index also exports the lazy SettingsPage,
// which would pull the whole page into the main chunk
import { settingReducer } from '@/pages/settings/model/settingSlice';
import { baseApi } from '@/shared/api/baseApi';
import { navReducer } from '@/shared/model/navSlice';

// "Remember Me" used to keep the password next to the email;
// drop it from the state that is already stored in the browser
const dropRememberedPassword = (state: PersistedState): PersistedState => {
  const stored = state as
    | (PersistedState & { auth?: { rememberMe?: { email?: string } | null } })
    | undefined;

  if (!stored?.auth?.rememberMe) return state;

  return {
    ...stored,
    auth: {
      ...stored.auth,
      rememberMe: { email: stored.auth.rememberMe.email },
    },
  } as PersistedState;
};

// Where the tokens of a session without "Remember Me" are kept: sessionStorage lives as long
// as the browser tab, so closing the browser ends the session (AU-6-04)
const SESSION_TOKENS_KEY = 'in_home:session';

type SessionTokens = Pick<AuthState, 'accessToken' | 'refreshToken'>;

const SIGNED_OUT: Partial<AuthState> = {
  accessToken: null,
  refreshToken: null,
  isAdmin: false,
  role: null,
  onboarding_finished: false,
  email_added: false,
};

const readSessionTokens = (): SessionTokens | null => {
  try {
    const stored = window.sessionStorage.getItem(SESSION_TOKENS_KEY);
    return stored ? (JSON.parse(stored) as SessionTokens) : null;
  } catch {
    return null;
  }
};

const writeSessionTokens = (tokens: SessionTokens | null) => {
  try {
    if (tokens?.refreshToken) {
      window.sessionStorage.setItem(SESSION_TOKENS_KEY, JSON.stringify(tokens));
    } else {
      window.sessionStorage.removeItem(SESSION_TOKENS_KEY);
    }
  } catch {
    // Storage blocked (private mode): the session then ends with the page
  }
};

// The sign-up link waiting for its password lives as long as the tab, like a session without
// "Remember Me": the link's code must not stay in localStorage
const PENDING_SIGN_UP_KEY = 'in_home:pending-sign-up';

const readPendingSignUp = (): string | null => {
  try {
    return window.sessionStorage.getItem(PENDING_SIGN_UP_KEY);
  } catch {
    return null;
  }
};

const writePendingSignUp = (code: string | null | undefined) => {
  try {
    if (code) {
      window.sessionStorage.setItem(PENDING_SIGN_UP_KEY, code);
    } else {
      window.sessionStorage.removeItem(PENDING_SIGN_UP_KEY);
    }
  } catch {
    // Storage blocked (private mode): the password step is then kept until a reload
  }
};

// Sessions stored before `sessionRemembered` existed are kept as remembered
const isRemembered = (auth: AuthState) => auth.sessionRemembered !== false;

// Keeps the tokens of a session without "Remember Me" out of localStorage (they go to
// sessionStorage), and makes every opening of the app check the session with the server
// first (AU-A-05): `isValidRefreshToken` is never restored from storage.
const sessionStorageTransform = createTransform<AuthState, AuthState>(
  (auth) => {
    writePendingSignUp(auth.pendingSignUpCode);
    if (isRemembered(auth)) {
      writeSessionTokens(null);
      return { ...auth, isValidRefreshToken: false, pendingSignUpCode: null };
    }
    writeSessionTokens({
      accessToken: auth.accessToken,
      refreshToken: auth.refreshToken,
    });
    // Only the tokens are kept out: the role is needed to pick the home page after a reload
    return {
      ...auth,
      accessToken: null,
      refreshToken: null,
      isValidRefreshToken: false,
      pendingSignUpCode: null,
    };
  },
  (auth) => {
    const restored = {
      ...auth,
      isValidRefreshToken: false,
      pendingSignUpCode: readPendingSignUp(),
    };
    if (isRemembered(restored)) return restored;

    const tokens = readSessionTokens();
    return tokens?.refreshToken
      ? { ...restored, ...tokens }
      : ({ ...restored, ...SIGNED_OUT } as AuthState);
  },
  { whitelist: ['auth'] },
);

const rootReducer = combineReducers({
  auth: authReducer,
  nav: navReducer,
  onBoarding: onboardingReducer,
  setting: settingReducer,
  [baseApi.reducerPath]: baseApi.reducer,
});

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'in_home',
  version: 15,
  migrate: createMigrate({ 15: dropRememberedPassword }),
  storage,
  whitelist: ['auth', 'setting'],
  transforms: [sessionStorageTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(baseApi.middleware),
});

export const persistor = persistStore(store);
